import { useEventCallback } from '@shared/utils/hooks/useEventCallback';
import { useIsMobileScreen, useIsMobileOrTablet } from '@shared/utils/media/useMediaScreens';
import React, { useCallback, useMemo, useRef, useState } from 'react';
import { ShopRootExpertsExploreCurationClicked } from '../../../../Catalog.telemetry.types';
import { useCatalogRouting } from '../../../../useCatalogRouting';
import { CurationHeroHeaderRootProps } from './CurationHeroHeader.types';
import { useRegistryOverviewTelemetry } from '@apps/registry/admin/routes/Overview/telemetry';
import { useCatalogLoggingContext } from '../../../../Catalog.logging';
import { useVimeoPlayerContext } from '@apps/registry/common/components/Catalog/utils/vimeoPlayer';

const useCurationHeroHeaderTelemetry = ({ curations }: { curations: CurationHeroHeaderRootProps['curations'] }) => {
  const {
    curationsHeroSectionCarouselItemClicked,
    curationsHeroSectionCarouselArrowClicked,
    watchCurationVideoClicked,
    curationsHeroSectionExploreCurationClicked,
    curationsHeroSectionBrowseAllClicked,
    getProductCatalogCategoryBasePageEventProps,
  } = useCatalogLoggingContext();

  const getCurationById = useEventCallback((curationId: string) => {
    return curations.find(curation => curation.id === curationId);
  });

  const trackCurationListItemClick = useEventCallback((curationId: string, index: number) => {
    const targetCuration = getCurationById(curationId);

    curationsHeroSectionCarouselItemClicked({
      curationId,
      curationTitle: targetCuration ? targetCuration.header.title : '<CURATION NOT FOUND>',
      itemPosition: index,
    });
  });

  const trackCurationListArrowClick = useEventCallback((direction: 'left' | 'right') => {
    curationsHeroSectionCarouselArrowClicked({ direction });
  });

  const trackBillboardWatchCurationVideoCtaClick = useEventCallback((curationId: string) => {
    const targetCuration = getCurationById(curationId);
    watchCurationVideoClicked({
      curationId,
      curationTitle: targetCuration ? targetCuration.header.title : '<CURATION NOT FOUND>',
      page: 'root',
      method: 'ctaButton',
    });
  });

  const trackExploreCurationClicked = useEventCallback((curationId: string, method: 'curationsHeroSection' | 'videoPlayer', isVideoPlaying: boolean) => {
    const targetCuration = getCurationById(curationId);
    curationsHeroSectionExploreCurationClicked({
      curationId,
      curationTitle: targetCuration ? targetCuration.header.title : '<CURATION NOT FOUND>',
      method,
      isVideoPlaying,
      ...getProductCatalogCategoryBasePageEventProps({ product: null, curation: targetCuration?.category }).categoryTelemArgs,
    });
  });

  return {
    trackCurationListItemClick,
    trackCurationListArrowClick,
    trackBillboardWatchCurationVideoCtaClick,
    trackExploreCurationClicked,
    trackShopRootExpertsBrowseAllClicked: curationsHeroSectionBrowseAllClicked,
  };
};

export const useCurationHeroHeaderController = (props: CurationHeroHeaderRootProps) => {
  const { curations } = props;
  const { trackCurationListItemClick, trackCurationListArrowClick, trackBillboardWatchCurationVideoCtaClick, trackExploreCurationClicked, trackShopRootExpertsBrowseAllClicked } =
    useCurationHeroHeaderTelemetry({
      curations,
    });
  const telemetry = useRegistryOverviewTelemetry();
  const [selectedCurationId, setSelectedCurationId] = useState(curations[0]?.id);

  const billboardRef = useRef<HTMLDivElement | null>(null);
  const curationListRef = useRef<HTMLDivElement | null>(null);

  const iframeRef = useRef<HTMLIFrameElement>(null);
  const { VimeoPlayer } = useVimeoPlayerContext();

  const handleOnCloseVideoDialog = useEventCallback(() => {
    VimeoPlayer && iframeRef.current && new VimeoPlayer(iframeRef.current).play();
  });

  const selectedCuration = useMemo(() => {
    return curations.find(curation => curation.id === selectedCurationId);
  }, [curations, selectedCurationId]);

  const isMobile = useIsMobileScreen();
  const isMobileOrTablet = useIsMobileOrTablet();

  const { goToRootCurations, goToCurationDetails } = useCatalogRouting();

  const handleOnCurationClick = useCallback(
    (curationId: string, index: number) => {
      setSelectedCurationId(curationId);
      trackCurationListItemClick(curationId, index);
    },
    [trackCurationListItemClick],
  );

  const handleOnSelectedCurationChange = useCallback((curationId: string) => {
    setSelectedCurationId(curationId);
  }, []);

  const handleOnViewAllClick = useCallback(() => {
    trackShopRootExpertsBrowseAllClicked();
    goToRootCurations();
  }, [goToRootCurations, trackShopRootExpertsBrowseAllClicked]);

  const handleOnViewCurationClick = useCallback<React.MouseEventHandler<HTMLButtonElement>>(
    e => {
      if (selectedCuration) {
        telemetry.registryOverviewInteraction({ component: 'Shop: Hero', method: 'ExploreClicked', label: selectedCuration.header.title || undefined });
        const { isvideoplaying, method } = e.currentTarget.dataset;
        trackExploreCurationClicked(selectedCuration.id, method as ShopRootExpertsExploreCurationClicked['method'], isvideoplaying === 'true');
        goToCurationDetails(`${selectedCuration.category.publicUrlPath}`, `?ref=sr_curations_hero_section`);
      }
    },
    [goToCurationDetails, selectedCuration, telemetry, trackExploreCurationClicked],
  );

  const handleOnOpenVideoPlayer = useCallback(
    (curationId: string) => {
      trackBillboardWatchCurationVideoCtaClick(curationId);
    },
    [trackBillboardWatchCurationVideoCtaClick],
  );

  const handleOnCurationListArrowClick = useCallback(
    (direction: 'left' | 'right') => {
      trackCurationListArrowClick(direction);
    },
    [trackCurationListArrowClick],
  );

  return {
    selectedCurationId,
    selectedCuration,
    isMobile,
    isMobileOrTablet,
    billboardRef,
    curationListRef,
    iframeRef,
    handleOnCloseVideoDialog,
    handleOnCurationClick,
    handleOnSelectedCurationChange,
    handleOnCurationListArrowClick,
    handleOnViewAllClick,
    handleOnViewCurationClick,
    handleOnOpenVideoPlayer,
  };
};
